/**
 * Admin "Test Mode" panel: one-click impersonation of test users.
 *
 * Each button generates an impersonation token and opens a NEW TAB
 * with that token applied via the /test-login URL, so the admin's
 * own session in this tab stays intact.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { TestTube, FileText, Sprout, User, AlertCircle, ExternalLink } from "lucide-react";

const ROLES = [
  {
    id: "applicant",
    title: "Test Application Flow",
    desc: "Walk through the Stage 3 grower application as a fresh applicant. Useful for QA on autosave, validation, signature pack and acceptance flow.",
    icon: FileText,
    btn: "Open application",
  },
  {
    id: "grower",
    title: "Test Grower Portal",
    desc: "Land on the active grower portal as the Test Grower fixture user. See the sticker dashboard, profile panel, settings, and all coming-soon panels.",
    icon: Sprout,
    btn: "Open grower portal",
  },
  {
    id: "subscriber",
    title: "Test Subscriber Portal",
    desc: "Land on the subscriber portal as the Test Subscriber. See tenure badges, dashboard, deliveries placeholders, billing and account panels.",
    icon: User,
    btn: "Open subscriber portal",
  },
];

export default function ConsoleTesting() {
  const [loading, setLoading] = useState(null);
  const [err, setErr] = useState("");

  const launch = async (role) => {
    setLoading(role);
    setErr("");
    try {
      const { data } = await api.post("/admin/test-mode/impersonate", { role });
      // Open new tab with the impersonation token via a tiny test-login route
      const url = `${window.location.origin}/test-login?token=${encodeURIComponent(data.access_token)}&redirect=${encodeURIComponent(data.redirect)}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start test session");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div data-testid="console-testing">
      <div className="mb-8">
        <div className="bloom-divider mb-4 text-left">✦ Test Mode ✦</div>
        <h1 className="font-serif text-5xl">Walk a mile in each role.</h1>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] mt-2">
          Spin up a fixture session for any portal in a new tab. Your admin session here stays intact.
        </p>
      </div>

      <div className="bloom-card p-5 mb-6 border-l-4 border-[var(--bloom-accent)] flex items-start gap-3">
        <TestTube className="text-[var(--bloom-accent)] flex-shrink-0 mt-0.5" size={18} />
        <div>
          <div className="font-serif text-base">Persistent test accounts</div>
          <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">
            The same Test Subscriber, Test Grower and Test Applicant fixtures are reused on every click. Anything you do in those sessions stays in the database so you can see how flows look after multiple steps.
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-6 p-4 border border-[var(--bloom-accent)] text-[var(--bloom-accent)] font-serif italic flex items-center gap-2">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const isLoading = loading === r.id;
          return (
            <div key={r.id} className="bloom-card p-6" data-testid={`test-card-${r.id}`}>
              <Icon className="text-[var(--bloom-brand)] mb-3" size={28} strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mb-2">{r.title}</h3>
              <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-5 leading-relaxed">
                {r.desc}
              </p>
              <button
                onClick={() => launch(r.id)}
                disabled={isLoading}
                className="bloom-btn bloom-btn-primary !text-xs"
                data-testid={`test-btn-${r.id}`}
              >
                {isLoading ? "Opening..." : (
                  <>
                    {r.btn} <ExternalLink size={12} className="ml-2 inline" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
